import { useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { Enums } from "@/services/dropdown-enums";
import UseGql from "@/services/graph-ql/useGql";
import { Get_Auth_Providers } from "../../leads/graphql-queries/email-integration";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import Lead_signalr from "../../leads/signalr-constants";
import guid from "guid";
import { useSelector , useDispatch } from "react-redux";
import { success, toastMsg } from "@/services/variables";
import {
  Get_Appointment_Calendars,
  Get_Connected_Calendars,
  Get_Appointment_Calendar_By_Id,
} from "../../leads/graphql-queries/appointment-scheduling";

import {
  setCalendarsIsFetchedToState,
  setCalendarsToState,
  setCalendarToState,
  setMembersToState,
} from "@/store/slices/appointment/appointmentSlice";
import { setCrmCalendarsToState } from "@/store/slices/general/dropdownOptionsSlice";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import { AuthModel } from "@blazeo.com/calendar-client";
import {
  rememberSelfConnectedParticipant,
  clearSelfConnectedParticipant,
  getSelfConnectedParticipantId,
} from "@/modules/crm/settings/components/details/content/appointment-scheduling/self-connected-member";

const {
  hubName,
  InvokeMethodNames: { connectCalendar, deleteMember },
  onReceiveMethodNames: {
    onConnectCalendarResponse,
    onDeleteMemberResponse,
    onInviteMember,
  },
} = Lead_signalr;

const resultMethods = {
  [onConnectCalendarResponse]: { successOperation: "connected" },
  [onDeleteMemberResponse]: { successOperation: "deleted" },
  [onInviteMember]: { successOperation: "invited" },
};
let methodKeys = Object.keys(resultMethods);

let initialLoad = true;
let calendarsPromise = null;
let membersPromise = null;
const useAuthProvider = ({ authType = "Mail", onSaveCB, fetchLiveStatus = false }) => {
  const { loading, executeQuery } = UseGql(Get_Connected_Calendars);
  const getAuthProviders = UseGql(Get_Auth_Providers);
  const getCalendarsGql = UseGql(Get_Appointment_Calendars);
  const getCalendarGql = UseGql(Get_Appointment_Calendar_By_Id);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarFetchingDone, setCalendarFetchingDone] = useState(false);
  const [membersFetchingDone, setMembersFetchingDone] = useState(false);
  const [authProviders, setAuthProviders] = useState({});
  const { statuses, RTC } = useSignalRClient({ hubName: hubName });
  const leadhub = RTC[hubName];
  const [submitting, setSubmitting] = useState(false);
  const { client: dataClient } = useCRMDataClient();
  const user = useSelector((state) => state.auth?.profile);
  const dispatch = useDispatch();
  const storedCalendars = useSelector((state) => state.appointment?.calendars);
  const storedMembers = useSelector((state) => state.appointment?.members);

  const formRef = useRef({});
  const listenerAddedRef = useRef(false); // Ref to track listener
  const onSaveCBRef = useRef(onSaveCB);
  onSaveCBRef.current = onSaveCB;

  const fetchAuthProviders = useCallback(() => {
    dataClient.CalendarAuth.getProviders()
      .then((res) => {
        if (res?.providers) {
          // Map to the existing format expected by the modal (using key as object key)
          const mapped = {};
          res.providers.forEach(p => {
             // UI expects 'microsoft' instead of 'outlook'
             const uiKey = p.type === 'outlook' ? 'microsoft' : p.type;
             mapped[uiKey] = p;
          });
          setAuthProviders(mapped);
        }
      })
      .catch((err) => console.log("onError:", err));
  }, [dataClient]);

  const calendarAuthUnsubRef = useRef(null);
  const directAuthPollRef = useRef(null);
  const authWaitCancelRef = useRef(null);

  const resetListener = () => {
    if (typeof authWaitCancelRef.current === "function") {
      authWaitCancelRef.current();
      authWaitCancelRef.current = null;
    }
    if (typeof calendarAuthUnsubRef.current === "function") {
      calendarAuthUnsubRef.current();
      calendarAuthUnsubRef.current = null;
    }
  };

  const checkEmailExist = async (email = "") => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (
      members.some(
        (member) => member.email?.trim().toLowerCase() === normalizedEmail
      )
    ) {
      return true;
    }

    if (!user?.CompanyKey) return false;
    try {
      const existing = await dataClient.CalendarAuth.getParticipantByEmail({
        email: normalizedEmail,
        companyKey: user.CompanyKey,
      });
      return Boolean(existing?.participantId);
    } catch (err) {
      console.log("checkEmailExist error:", err);
      return false;
    }
  };

  const openProvider = async (data) => {
    let { type, participantId, name, email } = data; // Strict adherence to participantId over email
    if (formRef.current) {
      formRef.current = data;
    }

    if (!type) return;

    // Existing participant (row Connect / invitee): keep getAuthorizationUrl flow.
    if (participantId) {
      if (!name || !email) return;

      setSubmitting(true);
      try {
        const loggedInUserId = user?.UserGUID || user?.userGUID;
        const { authorizationUrl } = await dataClient.CalendarAuth.getAuthorizationUrl({
          provider: type,
          participantId,
          authorizedByParticipantId: loggedInUserId,
        });
        if (!authorizationUrl) {
          throw new Error(`Failed to get authorizationUrl`);
        }
        await waitForExistingParticipantAuth(authorizationUrl, participantId);
      } catch (err) {
        console.error("Failed to generate auth URL", err);
        toast.error("Failed to generate authorization URL");
        setSubmitting(false);
      }
      return;
    }

    // No participant yet: startDirectAuthorization (backend creates participant after OAuth).
    // OLD no-participant path (name/email + addParticipant + getAuthorizationUrl) — kept for reference:
    /*
    if (!name || !email) return;

    if (!participantId) {
      const isEmailExist = await checkEmailExist(email);
      if (isEmailExist) {
        toast.error(toastMsg.errors.customError("Email already exists"));
        return;
      }
    }

    setSubmitting(true);
    try {
      let targetParticipantId = participantId;
      if (!targetParticipantId) {
        const loggedInUserId = user?.UserGUID || user?.userGUID;
        let participantRes = await dataClient.CalendarAuth.addParticipant({
          name,
          email,
          companyKey: user?.CompanyKey,
          authorizedByParticipantId: loggedInUserId,
        });

        if (!participantRes) {
          const existing = await dataClient.CalendarAuth.getParticipantByEmail({ email, companyKey: user?.CompanyKey });
          if (existing) {
            participantRes = { participantId: existing.participantId };
          } else {
            throw new Error("Failed to add participant and could not resolve existing member by email.");
          }
        }

        targetParticipantId = participantRes.participantId;
        if (formRef.current) {
           formRef.current.participantId = targetParticipantId;
        }
      }

      const loggedInUserId = user?.UserGUID || user?.userGUID;
      const { authorizationUrl } = await dataClient.CalendarAuth.getAuthorizationUrl({
        provider: type,
        participantId: targetParticipantId,
        authorizedByParticipantId: loggedInUserId,
      });
      if (!authorizationUrl) {
        throw new Error(`Failed to get authorizationUrl`);
      }
      openSignInWindow(authorizationUrl, type, targetParticipantId);
    } catch (err) {
      console.error("Failed to generate auth URL", err);
      toast.error("Failed to generate authorization URL");
      setSubmitting(false);
    }
    */

    if (!user?.CompanyKey) {
      toast.error("Company key is required to connect a calendar.");
      return;
    }

    setSubmitting(true);
    try {
      await startDirectAuthorizationFlow(type);
    } catch (err) {
      console.error("Failed to start direct authorization", err);
      toast.error("Failed to generate authorization URL");
      setSubmitting(false);
    }
  };

  // Module-level refs — must survive React re-renders
  // (declared outside JSX tree via useRef so mutations persist)
  const windowRef = useRef(null);
  const previousUrlRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Surgically update a member's approval status in the Redux store (no full refetch)
  const updateMemberApproval = (memberData) => {
    const id = memberData.id || memberData.participantId;
    const memberId = memberData.memberId || id;
    setMembers((prev) => {
      const idx = prev.findIndex(m => (m.id ?? m.participantId) === id);
      const updated = idx !== -1
        ? prev.map((m, i) => i === idx ? { ...m, ...memberData, memberId } : m)
        : [...prev, { ...memberData, memberId }];
      dispatch(setMembersToState(updated));
      return updated;
    });
  };

  const finishDirectAuth = () => {
    if (typeof authWaitCancelRef.current === "function") {
      authWaitCancelRef.current();
      authWaitCancelRef.current = null;
    }
    if (directAuthPollRef.current) {
      clearInterval(directAuthPollRef.current);
      directAuthPollRef.current = null;
    }
    const unsub = calendarAuthUnsubRef.current;
    calendarAuthUnsubRef.current = null;
    if (typeof unsub === "function") {
      unsub();
    }
    isProcessingRef.current = false;
    windowRef.current = null;
    setSubmitting(false);
  };

  const stampConnectedMember = (memberData, participantId) => {
    const loggedInUserId = user?.UserGUID || user?.userGUID;
    const normalized = dataClient.CalendarAuth._normalizeStatus({
      ...memberData,
      participantId: participantId || memberData.participantId || memberData.id,
      isApproved: true,
      connected: true,
      status: Enums.ConnectEmailStatus.Connected,
    });
    if (loggedInUserId) {
      normalized.authorizedByParticipantId =
        normalized.authorizedByParticipantId || loggedInUserId;
      normalized.AuthorizedByParticipantId =
        normalized.AuthorizedByParticipantId || loggedInUserId;
    }
    normalized.authorizationSource =
      normalized.authorizationSource ||
      normalized.AuthorizationSource ||
      "Direct";
    normalized.AuthorizationSource = normalized.authorizationSource;
    if (!normalized.email && memberData.email) {
      normalized.email = memberData.email;
    }
    if (!normalized.name) {
      normalized.name = memberData.alias || memberData.name || normalized.email;
    }
    const stampedId =
      normalized.participantId || normalized.id || participantId;
    if (stampedId) {
      rememberSelfConnectedParticipant(user, stampedId);
    }
    return normalized;
  };

  const withSelfConnectMeta = (normalized, member) => {
    const key = String(
      normalized.id ?? normalized.participantId ?? member?.id ?? ""
    );
    const prev = (storedMembers || []).find(
      (m) => String(m.id ?? m.participantId ?? "") === key
    );
    const storedSelfId = getSelfConnectedParticipantId(user);
    const isRememberedSelf = storedSelfId && storedSelfId === key;
    const source = isRememberedSelf
      ? "Direct"
      : normalized.authorizationSource ||
        normalized.AuthorizationSource ||
        prev?.authorizationSource ||
        prev?.AuthorizationSource;
    const loggedInUserId = user?.UserGUID || user?.userGUID;
    const authBy = isRememberedSelf
      ? loggedInUserId ||
        normalized.authorizedByParticipantId ||
        normalized.AuthorizedByParticipantId
      : normalized.authorizedByParticipantId ||
        normalized.AuthorizedByParticipantId ||
        prev?.authorizedByParticipantId ||
        prev?.AuthorizedByParticipantId;
    return {
      ...normalized,
      name: member.alias || member.name || member.email || normalized.name,
      status: normalized.status ?? member.status,
      ...(source
        ? { authorizationSource: source, AuthorizationSource: source }
        : {}),
      ...(authBy
        ? {
            authorizedByParticipantId: authBy,
            AuthorizedByParticipantId: authBy,
          }
        : {}),
    };
  };

  const notifyCalendarConnected = (memberData) => {
    if (memberData) {
      updateMemberApproval(memberData);
    }
    toast.success("Calendar connected successfully!");
    if (typeof onSaveCBRef.current === "function") {
      onSaveCBRef.current();
    }
  };

  
  /**
   * After direct OAuth. Toast + onSaveCB only when auth is confirmed:
   * postMessage success, or popup-close with isAuthorized / a new connected member.
   * Closing the consent screen without finishing must not redirect.
   */
  const completeDirectAuthSuccess = async (participantId, { fromMessage = false } = {}) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    try {
      if (participantId && formRef.current) {
        formRef.current.participantId = participantId;
      }
      if (participantId) {
        rememberSelfConnectedParticipant(user, participantId);
      }

      let memberData = null;
      let isAuthorized = null;
      if (participantId) {
        try {
          const check = await dataClient.CalendarAuth.getAuthorizationStatus(participantId);
          if (check?.isAuthorized === false) {
            clearSelfConnectedParticipant(user, participantId);
            toast.error(
              check?.authorizationError || "Calendar was not authorized."
            );
            return;
          }
          isAuthorized = check?.isAuthorized === true;
          let statusPayload = check || {};
          try {
            const response = await dataClient.CalendarAuth.getStatus(participantId);
            statusPayload = { ...check, ...response };
          } catch (statusErr) {
            console.error("[directAuth] getStatus failed", statusErr);
          }
          memberData = stampConnectedMember(statusPayload, participantId);
        } catch (verifyErr) {
          console.error("[directAuth] verify failed", verifyErr);
        }
      }

      if (dataClient?.appointments?.clearCache) {
        dataClient.appointments.clearCache();
      }

      const prevIds = new Set(formRef.current?.memberIdsSnapshot || []);
      const findMatched = (list) =>
        (participantId &&
          list.find(
            (m) => String(m.participantId || m.id) === String(participantId)
          )) ||
        list.find((m) => {
          const id = String(m.id ?? m.participantId ?? "");
          const connected =
            m.status === Enums.ConnectEmailStatus.Connected ||
            m.isApproved ||
            m.isAuthorized;
          return connected && id && !prevIds.has(id);
        });

      let list = [];
      let matched = null;
      const attempts = fromMessage || participantId ? 3 : 2;
      for (let attempt = 0; attempt < attempts; attempt++) {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, 1500));
          if (dataClient?.appointments?.clearCache) {
            dataClient.appointments.clearCache();
          }
        }
        try {
          list = (await fetchMembers(true)) || [];
        } catch (fetchErr) {
          console.error("[directAuth] fetchMembers failed", fetchErr);
        }
        matched = findMatched(list);
        if (matched) break;
      }

      if (matched) {
        memberData = stampConnectedMember(
          matched,
          participantId || matched.participantId || matched.id
        );
      }

      const confirmed =
        fromMessage ||
        isAuthorized === true ||
        Boolean(matched);

      if (confirmed) {
        notifyCalendarConnected(memberData);
      }
    } catch (e) {
      console.error("[directAuth] completeDirectAuthSuccess failed", e);
      toast.error(e?.message || "Calendar authorization failed");
    } finally {
      finishDirectAuth();
    }
  };

  /**
   * No-participant connect: startDirectAuthorization + waitForDirectAuthorization.
   * Races postMessage against Auth/DirectAuthorizationStatus so COOP (cleared
   * window.opener) still reports success/error. Do not use popup.closed.
   */
  const startDirectAuthorizationFlow = async (provider) => {
    const startRes = await dataClient.CalendarAuth.startDirectAuthorization({
      companyKey: user.CompanyKey,
      provider,
    });
    if (startRes.status && startRes.status !== "success") {
      throw new Error(startRes.message || "Failed to start calendar authorization");
    }
    const authorizationUrl = startRes.authorizationUrl;
    const requestId = startRes.requestId || startRes.data?.requestId;
    if (!authorizationUrl) {
      throw new Error("Failed to get authorizationUrl");
    }
    if (!requestId) {
      throw new Error("Failed to get authorization requestId");
    }

    let snapshotSource = storedMembers?.length ? storedMembers : members || [];
    try {
      const existing = await fetchMembers(true);
      if (existing?.length) snapshotSource = existing;
    } catch (snapErr) {
      console.error("[directAuth] member snapshot failed", snapErr);
    }
    formRef.current = {
      ...(formRef.current || {}),
      type: provider,
      requestId,
      memberIdsSnapshot: snapshotSource
        .map((m) => String(m.id ?? m.participantId ?? ""))
        .filter(Boolean),
    };

    if (typeof authWaitCancelRef.current === "function") {
      authWaitCancelRef.current();
      authWaitCancelRef.current = null;
    }

    let popup = AuthModel.openOAuthPopup(authorizationUrl);
    if (popup && typeof popup.then === "function") {
      popup = await popup;
    }
    if (!popup) {
      toast.error("Please allow popups to connect your calendar.");
      finishDirectAuth();
      return;
    }
    windowRef.current = popup;

    const { promise, cancel } = AuthModel.waitForDirectAuthorization(
      requestId,
      null
    );
    authWaitCancelRef.current = cancel;

    let waitSettled = false;
    const abandoned = new Promise((resolve) => {
      const id = setInterval(() => {
        if (waitSettled) {
          clearInterval(id);
          return;
        }
        try {
          if (!popup || popup.closed) {
            clearInterval(id);
            resolve({ status: "closed" });
          }
        } catch (_) {}
      }, 1000);
    });
    const result = await Promise.race([promise, abandoned]);
    waitSettled = true;
    authWaitCancelRef.current = null;

    if (result.status === "closed") {
      cancel();
      finishDirectAuth();
      return;
    }

    if (result.status === "success") {
      await completeDirectAuthSuccess(result.participantId, { fromMessage: true });
    } else if (result.status === "error") {
      toast.error(result.message || "Calendar authorization failed");
      finishDirectAuth();
    } else {
      finishDirectAuth();
    }
  };

  /**
   * Existing participant (row Connect): waitForAuthorization races postMessage
   * against Auth/Status (getAuthorizationStatus) when COOP drops opener.
   */
  const waitForExistingParticipantAuth = async (authorizationUrl, participantId) => {
    if (formRef.current) {
      formRef.current.participantId = participantId;
    }

    if (typeof authWaitCancelRef.current === "function") {
      authWaitCancelRef.current();
      authWaitCancelRef.current = null;
    }

    let popup = AuthModel.openOAuthPopup(authorizationUrl);
    if (popup && typeof popup.then === "function") {
      popup = await popup;
    }
    if (!popup) {
      toast.error("Please allow popups to connect your calendar.");
      setSubmitting(false);
      return;
    }
    windowRef.current = popup;

    const { promise, cancel } = AuthModel.waitForAuthorization(
      participantId,
      null
    );
    authWaitCancelRef.current = cancel;

    let waitSettled = false;
    const abandoned = new Promise((resolve) => {
      const id = setInterval(() => {
        if (waitSettled) {
          clearInterval(id);
          return;
        }
        try {
          if (!popup || popup.closed) {
            clearInterval(id);
            resolve({ status: "closed" });
          }
        } catch (_) {}
      }, 1000);
    });
    const result = await Promise.race([promise, abandoned]);
    waitSettled = true;
    authWaitCancelRef.current = null;

    if (result.status === "closed") {
      cancel();
      finishDirectAuth();
      return;
    }

    if (result.status === "success") {
      await completeDirectAuthSuccess(participantId, { fromMessage: true });
    } else if (result.status === "error") {
      toast.error(result.message || "Calendar authorization failed");
      try {
        await dataClient.appointments.deleteMember({ id: participantId });
        removeMemberFromList(participantId);
      } catch (delErr) {
        console.error("Failed to silently delete unauthorized participant:", participantId, delErr);
      }
      finishDirectAuth();
    } else {
      finishDirectAuth();
    }
  };

  // NOTE: receiveMessage is unused. Completion uses AuthModel.waitForDirectAuthorization
  // / waitForAuthorization (postMessage + DirectAuthorizationStatus / Status poll).
  const receiveMessage = () => {};

  const onSave = (data) => {
    if (statuses[hubName] && leadhub) {
      let methodName = connectCalendar;
      let payload = createPayload(data);
      console.log("payload ::", payload);
      onSend(methodName, payload);
    }
  };

  const onSend = (methodName, payload) => {
    setSubmitting(true);
    const responseMethodName = onConnectCalendarResponse;
    const handler = (response) => {
      leadhub.off(responseMethodName, handler);
      setSubmitting(false);
      if (response.isSuccess) onSuccess("connected");
      else onError(response.errors[0]);
    };
    leadhub.on(responseMethodName, handler);
    leadhub.invoke(methodName, payload).catch((err) => {
      leadhub.off(responseMethodName, handler);
      setSubmitting(false);
      onError(err.message);
    });
  };

  const createPayload = (data) => {
    let values = formRef?.current || {};
    let payload = {
      code: data?.code,
      member: {
        name: values?.name,
        email: values?.email,
        emailProviderType: Enums.EmailProviderType[values?.type],
      },
      requestId: guid.raw(),
    };
    return payload;
  };

  const [members, setMembers] = useState([]);
  // const fetchMembers = async () => {
  //   return getMembersGql
  //     .executeQuery()
  //     .then((res) => {
  //       let { data } = res;
  //       if (data?.members?.items?.length) {
  //         setMembers(data?.members?.items);
  //         dispatch(setMembersToState(data?.members?.items));
  //         return data.members?.items;
  //       }
  //       setMembers([]);
  //       setMembersFetchingDone(true)
  //       return [];
  //     })
  //     .catch((err) => {
  //       setMembersFetchingDone(true)
  //       console.log("onError:", err)
  //     });
  // };

  const fetchMembers = async (force = false) => {
    if (!force && storedMembers?.length > 0) {
      setMembers(storedMembers);
      setMembersFetchingDone(true);

      if (fetchLiveStatus) {
        // Refresh authorization via getStatus without refetching the full members list
        try {
          const updatedMembersList = await Promise.all(
            storedMembers.map(async (member) => {
              let updatedStatus;
              const memberId = member.participantId || member.id;
              if (memberId) {
                try {
                  updatedStatus = await dataClient.CalendarAuth.getStatus(memberId);
                } catch (e) {
                  console.error(
                    "fetchMembers (cached): getStatus failed for member",
                    memberId,
                    e
                  );
                  updatedStatus = dataClient.CalendarAuth._normalizeStatus(member);
                }
              } else {
                updatedStatus = dataClient.CalendarAuth._normalizeStatus(member);
              }
              const normalized = dataClient.CalendarAuth._normalizeStatus({
                ...member,
                ...updatedStatus,
              });
              return withSelfConnectMeta(normalized, member);
            })
          );
          setMembers(updatedMembersList);
          dispatch(setMembersToState(updatedMembersList));
          return updatedMembersList;
        } catch (err) {
          console.error("fetchMembers (cached): failed to update statuses", err);
          return storedMembers;
        }
      }

      return storedMembers;
    }

    if (membersPromise && !force) {
      return membersPromise;
    }

    membersPromise = (async () => {
      try {
        const params = { companyKey: user?.CompanyKey };
        const data = await dataClient.appointments.getMembers(params);
        if (data?.members?.items) {
          let membersList;
          if (fetchLiveStatus) {
            membersList = await Promise.all(data.members.items?.map(async (member) => {
              let updatedStatus;
              const memberId = member.participantId || member.id;
              if (memberId) {
                try {
                  updatedStatus = await dataClient.CalendarAuth.getStatus(memberId);
                } catch (e) {
                  console.error("fetchMembers: getStatus failed for member", memberId, e);
                  updatedStatus = dataClient.CalendarAuth._normalizeStatus(member);
                }
              } else {
                updatedStatus = dataClient.CalendarAuth._normalizeStatus(member);
              }
              const normalized = dataClient.CalendarAuth._normalizeStatus({
                ...member,
                ...updatedStatus,
              });
              return withSelfConnectMeta(normalized, member);
            }));
          } else {
            membersList = data.members.items?.map((member) => {
              const normalized = dataClient.CalendarAuth._normalizeStatus(member);
              return withSelfConnectMeta(normalized, member);
            });
          }
          setMembers(membersList);
          dispatch(setMembersToState(membersList));
          setMembersFetchingDone(true);
          return membersList;
        }
        setMembers([]);
        setMembersFetchingDone(true);
        return [];
      } catch (err) {
        setMembersFetchingDone(true);
        console.log("onError:", err);
        return [];
      } finally {
        setMembersFetchingDone(true);
        membersPromise = null;
      }
    })();
    return membersPromise;
  };

  const removeMemberFromList = (id) => {
    setMembers((prev) => {
      const updated = prev.filter(
        (m) => String(m.id ?? m.memberId ?? m.calendarId) !== String(id)
      );
      dispatch(setMembersToState(updated));
      return updated;
    });
  };

  const handleDeleteMember = async (id) => {
    if (id) {
       dataClient.appointments.deleteMember({ 
         id, 
         requestId: guid.raw(),
         appointmentHub: leadhub,
         methodName: deleteMember,
         responseMethodName: onDeleteMemberResponse
       })
        .then(() => {
          clearSelfConnectedParticipant(user, id);
          removeMemberFromList(id);
          toast.success(success.saveSuccess("Member", "deleted"));
        })
        .catch((err) => {
          onError(err.message);
        });
    }
  };


  const onSuccess = (operation, refetch = false) => {
    fetchMembers(true);
    toast.success(success.saveSuccess("Member", operation));
  };

  const onError = (errorMsg) => {
    toast.error(toastMsg.errors.customError(errorMsg));
  };

  const [calendars, setCalendars] = useState([]);

  const fetchCalendars = async (force = false) => {
    if (!force && storedCalendars?.length > 0) {
      setCalendarFetchingDone(true);
      return storedCalendars;
    }

    if (calendarsPromise && !force) {
      return calendarsPromise;
    }

    calendarsPromise = (async () => {
      try {
        const params = { query: Get_Appointment_Calendars, companyKey: user?.CompanyKey };
        const data = await dataClient.appointments.getCalendars(params);
        if (data?.calendars?.items) {
          const items = data.calendars.items;
          setCalendars(items);
          dispatch(setCalendarsToState(items));
        } else {
          setCalendars([]);
          dispatch(setCalendarsToState([]));
        }
        setCalendarFetchingDone(true);
        dispatch(setCalendarsIsFetchedToState(true));
        return data?.calendars?.items || [];
      } catch (err) {
        console.error("fetchCalendars Error:", err);
        setCalendarFetchingDone(true);
        dispatch(setCalendarsIsFetchedToState(true));
        return [];
      } finally {
        calendarsPromise = null;
      }
    })();

    return calendarsPromise;
  };

  // Same paginated calendars query the CRM shell uses for the Lead Grid
  // Appointment Type filter. Only write the store after a successful fetch so
  // a failed refresh does not clear options that are already loaded.
  const refreshCrmCalendars = async () => {
    try {
      let allItems = [];
      let totalCount = 0;
      let skip = 0;
      const take = 100;

      while (true) {
        const response = await getCalendarsGql.executeQuery({ skip, take });
        const page = response?.data?.calendars;
        // Non-CRM sessions resolve an empty payload without querying. Leave
        // the existing filter options in place in that case.
        if (!page) return null;
        const items = page?.items;
        if (items?.length) {
          allItems = allItems.concat(items);
          totalCount = page.totalCount;
          if (allItems.length >= totalCount) break;
          skip += items.length;
        } else {
          break;
        }
      }

      dispatch(setCrmCalendarsToState(allItems));
      return allItems;
    } catch (err) {
      console.error("refreshCrmCalendars Error:", err);
      return null;
    }
  };

  const fetchCalendar = async (calendar) => {
    return dataClient.appointments.getCalendarById(calendar)
      .then((data) => {
        if (data?.calendar) {
          dispatch(setCalendarToState(data?.calendar));
          return data?.calendar;
        }
        return null;
      })
      .catch((err) => console.log("onError:", err));
  };

  return {
    loading,
    fetchAuthProviders,
    resetListener,
    authProviders,
    openProvider,
    submitting,
    formRef,
    setMembers,
    members,
    fetchMembers,
    handleDeleteMember,
    calendars,
    setCalendars,
    fetchCalendars,
    refreshCrmCalendars,
    calendarLoading,
    calendarFetchingDone,
    fetchCalendar,
    loadingCalender: getCalendarGql.loading,
    membersFetchingDone,
    checkEmailExist,
    setMembersFetchingDone
  };
};

export default useAuthProvider;
